// Renders an audit log row into a human sentence: "Jane Smith created roster
// for Christina Doe on AC1 · Aug 10 – 31". The API stores structured facts
// only (action, entityType, entityLabel, details); all phrasing lives here so
// it can improve without a data migration.

import dayjs from 'dayjs'

// Per-action facts snapshotted at write time (see apps/api/src/lib/audit).
// Every field is optional — lookups are best-effort and v1 rows have null.
export type AuditLogDetails = {
  crewName?: string
  aircraftRegistration?: string
  tripName?: string
  ownerName?: string
  category?: string
  route?: string
  departureDateTime?: string
  startDate?: string
  endDate?: string
  date?: string
  dutyStatus?: string
  durationDays?: number
  count?: number
}

export type AuditLogEntry = {
  id: string
  actorName: string
  action: string
  entityType: string
  entityId: string | null
  entityLabel: string | null
  details?: AuditLogDetails | null
  createdAt: string | Date
}

// Verb phrase per action verb (the part after the namespace). Longest match
// wins via the explicit action map below; these cover the generic verbs.
const VERB_PHRASES: Record<string, string> = {
  create: 'created',
  quickAdd: 'added',
  add: 'added',
  update: 'updated',
  upsert: 'updated',
  save: 'updated',
  set: 'updated',
  delete: 'deleted',
  remove: 'removed',
  archive: 'archived',
  unarchive: 'unarchived',
  restore: 'restored',
  publish: 'published',
  unpublish: 'unpublished',
  submit: 'submitted',
  start: 'started',
  attach: 'attached',
  detach: 'detached',
  duplicate: 'duplicated',
  resend: 'resent',
  retry: 'retried',
  revert: 'reverted',
  deactivate: 'deactivated',
  reactivate: 'reactivated',
  complete: 'completed',
}

// Full-action overrides where the generic verb + entity type reads poorly.
const ACTION_PHRASES: Record<string, string> = {
  'trip.addLeg': 'added a flight to trip',
  'trip.updateLeg': 'updated a flight on trip',
  'trip.deleteLeg': 'deleted a flight from trip',
  'trip.setLegPaxBoarded': 'updated boarding on trip',
  'trip.setTripState': 'changed the state of trip',
  'trip.addNote': 'added a note to trip',
  'trip.updateNote': 'updated a note on trip',
  'trip.deleteNote': 'deleted a note from trip',
  'trip.addCrewNote': 'added a crew note to trip',
  'trip.updateCrewNote': 'updated a crew note on trip',
  'trip.deleteCrewNote': 'deleted a crew note from trip',
  'trip.updateComplianceDocuments': 'updated compliance documents on trip',
  'trip.updateLegComplianceDocuments': 'updated compliance documents on trip',
  'crew.setAdmin': 'changed admin access for',
  'crew.setPhoto': 'updated the photo of',
  'crew.resendInvite': 'resent the invite to',
  'crew.resendAllInvites': 'resent all crew invites',
  'crew.deactivateMe': 'deactivated their own account',
  'aircraft.setPhoto': 'updated the photo of aircraft',
  'aircraft.addDocument': 'added a document to aircraft',
  'aircraft.removeDocument': 'removed a document from aircraft',
  'aircraft.addDevicePassword': 'added a device password to aircraft',
  'aircraft.removeDevicePassword': 'removed a device password from aircraft',
  'roster.deleteByRange': 'cleared roster entries',
  'roster.updateStatus': 'updated a roster entry',
  'flightLog.upsertDraft': 'saved a flight log draft',
  'flightLog.submit': 'submitted a flight log',
  'task.updateItems': 'updated checklist items on a task',
  'company.update': 'updated company details for',
}

// Rich sentence templates using write-time details. Tried FIRST; returning
// undefined falls through to ACTION_PHRASES / verb phrasing, so v1 rows
// (details = null) and failed lookups keep the plainer sentence.
const ACTION_TEMPLATES: Record<string, (d: AuditLogDetails, e: AuditLogEntry) => string | undefined> = {
  'roster.create': (d) => {
    if (!d.crewName) return undefined
    return joinParts([
      `created roster for ${d.crewName}`,
      d.aircraftRegistration && `on ${d.aircraftRegistration}`,
      formatDateRange(d.startDate, d.endDate) && `· ${formatDateRange(d.startDate, d.endDate)}`,
      d.dutyStatus && `(${formatDutyStatus(d.dutyStatus)})`,
    ])
  },
  'roster.update': (d) => {
    if (!d.crewName) return undefined
    return joinParts([
      `updated roster for ${d.crewName}`,
      d.aircraftRegistration && `on ${d.aircraftRegistration}`,
      d.date && `· ${formatShortDate(d.date)}`,
      d.dutyStatus && `(${formatDutyStatus(d.dutyStatus)})`,
    ])
  },
  'roster.delete': (d) => {
    if (!d.crewName) return undefined
    return joinParts([
      `deleted a roster day for ${d.crewName}`,
      d.date && `· ${formatShortDate(d.date)}`,
    ])
  },
  'roster.updateStatus': (d) => {
    if (!d.crewName) return undefined
    return joinParts([
      `updated roster for ${d.crewName}`,
      d.date && `· ${formatShortDate(d.date)}`,
      d.dutyStatus && `(${formatDutyStatus(d.dutyStatus)})`,
    ])
  },
  'roster.deleteByRange': (d) => {
    if (!d.crewName) return undefined
    return joinParts([
      `cleared roster for ${d.crewName}`,
      formatDateRange(d.startDate, d.endDate) && `· ${formatDateRange(d.startDate, d.endDate)}`,
    ])
  },
  'trip.addLeg': (d) => legSentence('added a flight to', d),
  'trip.updateLeg': (d) => legSentence('updated a flight on', d),
  'trip.deleteLeg': (d) => legSentence('deleted a flight from', d),
  'trip.setLegPaxBoarded': (d) => legSentence('updated boarding on', d),
  'maintenance.create': (d) => maintenanceSentence('scheduled', d),
  'maintenance.update': (d) => maintenanceSentence('updated', d),
  'maintenance.delete': (d) => maintenanceSentence('deleted', d),
  'crew.addDocument': (d, e) =>
    d.crewName ? `added document ${e.entityLabel ?? ''} for ${d.crewName}`.trim() : undefined,
  'crew.removeDocument': (d, e) =>
    d.crewName ? `removed document ${e.entityLabel ?? ''} from ${d.crewName}`.trim() : undefined,
  'aircraft.addDocument': (d, e) =>
    d.aircraftRegistration
      ? `added document ${e.entityLabel ?? ''} to aircraft ${d.aircraftRegistration}`.trim()
      : undefined,
  'aircraft.removeDocument': (d, e) =>
    d.aircraftRegistration
      ? `removed document ${e.entityLabel ?? ''} from aircraft ${d.aircraftRegistration}`.trim()
      : undefined,
  'document.create': (d, e) =>
    d.ownerName && e.entityLabel ? `added document ${e.entityLabel} for ${d.ownerName}` : undefined,
  'document.update': (d, e) =>
    d.ownerName && e.entityLabel ? `updated document ${e.entityLabel} for ${d.ownerName}` : undefined,
  'document.delete': (d, e) =>
    d.ownerName && e.entityLabel ? `deleted document ${e.entityLabel} for ${d.ownerName}` : undefined,
  'task.start': (d, e) => taskSentence('started', d, e),
  'task.submit': (d, e) => taskSentence('submitted', d, e),
}

function legSentence(verbPhrase: string, d: AuditLogDetails): string | undefined {
  if (!d.tripName) return undefined
  const extras = [d.route, d.aircraftRegistration].filter(Boolean).join(', ')
  return joinParts([`${verbPhrase} trip ${d.tripName}`, extras && `(${extras})`])
}

function maintenanceSentence(verb: string, d: AuditLogDetails): string | undefined {
  if (!d.aircraftRegistration) return undefined
  return joinParts([
    `${verb} maintenance for ${d.aircraftRegistration}`,
    d.startDate && `from ${formatShortDate(d.startDate)}`,
    d.durationDays != null && `(${d.durationDays} ${d.durationDays === 1 ? 'day' : 'days'})`,
  ])
}

function taskSentence(verb: string, d: AuditLogDetails, e: AuditLogEntry): string | undefined {
  if (!e.entityLabel) return undefined
  return joinParts([
    `${verb} task ${e.entityLabel}`,
    d.aircraftRegistration && `on ${d.aircraftRegistration}`,
    d.tripName && `· trip ${d.tripName}`,
  ])
}

function joinParts(parts: (string | false | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

/** "Aug 10 – 31" same-month, "Aug 30 – Sep 2" cross-month, single date otherwise. */
export function formatDateRange(startDate?: string, endDate?: string): string | undefined {
  if (!startDate) return undefined
  const start = dayjs(startDate)
  if (!start.isValid()) return undefined
  if (!endDate || endDate === startDate) return start.format('MMM D')
  const end = dayjs(endDate)
  if (!end.isValid()) return start.format('MMM D')
  if (start.isSame(end, 'month')) return `${start.format('MMM D')} – ${end.format('D')}`
  return `${start.format('MMM D')} – ${end.format('MMM D')}`
}

function formatShortDate(date: string): string {
  const parsed = dayjs(date)
  return parsed.isValid() ? parsed.format('MMM D') : date
}

/** "OnDuty" → "On Duty", "OffDuty" → "Off Duty". */
export function formatDutyStatus(dutyStatus: string): string {
  return dutyStatus.replace(/([a-z])([A-Z])/g, '$1 $2')
}

// Display names for entity types (default: the namespace itself).
const ENTITY_TYPE_LABELS: Record<string, string> = {
  trip: 'Trip',
  crew: 'Crew Member',
  aircraft: 'Aircraft',
  passenger: 'Passenger',
  document: 'Document',
  maintenance: 'Maintenance Record',
  roster: 'Roster',
  setting: 'Setting',
  devicePassword: 'Device Password',
  task: 'Task',
  checklist: 'Checklist Template',
  announcement: 'Announcement',
  flightLog: 'Flight Log',
  flightFolioScan: 'Folio Scan',
  company: 'Company',
}

export function formatEntityType(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType
}

/** "updated Trip Geneva" — the sentence minus the actor. */
export function formatAuditAction(entry: AuditLogEntry): string {
  const target = entry.entityLabel ?? undefined

  // Rich template first — needs write-time details; falls through when absent.
  if (entry.details) {
    const rich = ACTION_TEMPLATES[entry.action]?.(entry.details, entry)
    if (rich) return rich
  }

  const override = ACTION_PHRASES[entry.action]
  if (override) return target ? `${override} ${target}` : override

  const verb = entry.action.split('.').pop() ?? entry.action
  const verbKey = Object.keys(VERB_PHRASES)
    .sort((a, b) => b.length - a.length)
    .find((key) => verb === key || (verb.startsWith(key) && /[A-Z0-9]/.test(verb[key.length] ?? '')))
  const phrase = verbKey ? VERB_PHRASES[verbKey] : 'changed'

  const entityName = formatEntityType(entry.entityType).toLowerCase()
  return target ? `${phrase} ${entityName} ${target}` : `${phrase} a ${entityName}`
}
